// src/pages/Orders/OrderList.jsx - UPDATED WITH NEW REFUND STATUS NAMES
// ✅ All refund statuses updated to use REFUND_ prefix
// ✅ Payment-order synchronization features integrated
// ✅ Auto-progression notifications
// ✅ Payment validation before actions
// ✅ Refund method selection before CONFIRMED status

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllOrdersForAdminThunk,
  updateOrderStatusThunk,
  updatePaymentStatusThunk,
  getOrderStatsThunk,
  completePickupThunk,
  cancelRefundThunk,
} from "../../store/thunks/orderThunks";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import Modal from "../../components/common/Modal";
import {
  notifySuccess,
  notifyError,
  notifyInfo,
} from "../../utils/notification";
import {
  UserIcon,
  ChevronRightIcon,
  SearchIcon,
  FilterIcon,
  RefreshIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TruckIcon,
  ShoppingBagIcon,
  CurrencyRupeeIcon,
  ViewGridIcon,
  ViewListIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  LocationMarkerIcon,
  DotsVerticalIcon,
} from "@heroicons/react/outline";
import { BASE_API_URL } from "services/api";

const OrderList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state with default values to prevent undefined errors
  const {
    orders = [],
    loadingList = false,
    orderStats = null,
    pagination = { total: 0, page: 1, limit: 20, pages: 0 },
  } = useSelector((state) => state.orders || {});

  const { selectedBranch = null, branches = [] } = useSelector(
    (state) => state.branch || {}
  );

  // Local state
  const [viewMode, setViewMode] = useState("grid");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    paymentStatus: "",
    approvalStatus: "",
    refundStatus: "",
    branchCode: selectedBranch || "",
    fromDate: "",
    toDate: "",
    page: 1,
    limit: 20,
  });

  // ✅ NEW: Refund method modal state
  const [showRefundMethodModal, setShowRefundMethodModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [refundMethod, setRefundMethod] = useState("CASH");
  const [refundBankDetails, setRefundBankDetails] = useState({
    accountName: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
  });
  const [refundUpiDetails, setRefundUpiDetails] = useState({
    vpa: "",
    verifiedName: "",
  });

  // Load data
  const loadData = useCallback(() => {
    const filterParams = { ...filters };
    Object.keys(filterParams).forEach((key) => {
      if (!filterParams[key]) delete filterParams[key];
    });
    dispatch(getAllOrdersForAdminThunk(filterParams));
  }, [dispatch, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load stats
  useEffect(() => {
    const statsFilter = {};
    if (filters.branchCode) statsFilter.branchCode = filters.branchCode;
    if (filters.fromDate) statsFilter.fromDate = filters.fromDate;
    if (filters.toDate) statsFilter.toDate = filters.toDate;
    dispatch(getOrderStatsThunk(statsFilter));
  }, [dispatch, filters.branchCode, filters.fromDate, filters.toDate]);

  // Update filters when branch changes
  useEffect(() => {
    if (selectedBranch) {
      setFilters((prev) => ({ ...prev, branchCode: selectedBranch }));
    }
  }, [selectedBranch]);

  // ============================================================================
  // 🔥 ENHANCED: Payment Status Check Function
  // ============================================================================
  const checkPaymentStatus = async (orderId) => {
    try {
      const response = await fetch(
        `${BASE_API_URL}/orders/admin/order/${orderId}/payment-status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payment status");
      }

      return await response.json();
    } catch (error) {
      console.error("Payment status check error:", error);
      return {
        data: {
          allowedActions: {},
          warnings: [],
          blockers: [],
        },
      };
    }
  };

  // ============================================================================
  // 🔥 NEW: Handle Refund Method Confirmation
  // ============================================================================
  const handleConfirmWithRefundMethod = () => {
    // Validate based on selected method
    if (refundMethod === "BANK_TRANSFER") {
      if (
        !refundBankDetails.accountName ||
        !refundBankDetails.accountNumber ||
        !refundBankDetails.ifscCode ||
        !refundBankDetails.bankName
      ) {
        notifyError("Please fill all bank details");
        return;
      }
    } else if (refundMethod === "UPI") {
      if (!refundUpiDetails.vpa) {
        notifyError("Please enter UPI ID");
        return;
      }
    }

    // Find the order to get refund details
    const order = orders.find((o) => o.orderId === pendingOrderId);
    if (!order) return;

    // Prepare refund details
    const refundDetails = {
      refundMethod,
      refundReason:
        order.refundDetails?.refundReason || "EXCESS_EXCHANGE_VALUE",
    };

    if (refundMethod === "BANK_TRANSFER") {
      refundDetails.bankDetails = refundBankDetails;
    } else if (refundMethod === "UPI") {
      refundDetails.upiDetails = refundUpiDetails;
    }

    // Close modal and proceed with status update
    setShowRefundMethodModal(false);

    // Reset refund form
    setRefundMethod("CASH");
    setRefundBankDetails({
      accountName: "",
      accountNumber: "",
      ifscCode: "",
      bankName: "",
    });
    setRefundUpiDetails({
      vpa: "",
      verifiedName: "",
    });

    // Continue with status update
    handleUpdateStatusWithRefund(
      pendingOrderId,
      pendingStatusChange,
      refundDetails
    );
  };

  // ============================================================================
  // 🔥 ENHANCED: Status Update Handler with Payment & Refund Validation
  // ============================================================================
  const handleUpdateStatus = async (orderId, newStatus, options = {}) => {
    try {
      // ✅ NEW: Check if order has refund due and status is being changed to CONFIRMED
      if (newStatus === "CONFIRMED" && !options.refundDetailsProvided) {
        const order = orders.find((o) => o.orderId === orderId);

        if (order?.refundDetails?.required) {
          // Store pending status change and show refund method modal
          setPendingStatusChange(newStatus);
          setPendingOrderId(orderId);
          setShowRefundMethodModal(true);
          return;
        }
      }

      // ✅ Check payment before DELIVERED
      if (newStatus === "DELIVERED" && !options.skipPaymentCheck) {
        const paymentCheck = await checkPaymentStatus(orderId);

        if (!paymentCheck.data?.allowedActions?.canDeliver) {
          const blocker = paymentCheck.data?.blockers?.find(
            (b) => b.level === "CRITICAL_BLOCKER"
          );

          if (blocker) {
            const shouldNavigate = window.confirm(
              `🚫 ${blocker.message}\n\n${blocker.action}\n\nGo to order details to complete payment?`
            );

            if (shouldNavigate) {
              navigate(`/orders/${orderId}`);
            }
            return;
          }
        }
      }

      // ✅ Check for warnings before CONFIRMED (only if no refund)
      if (
        newStatus === "CONFIRMED" &&
        !options.forceConfirm &&
        !options.refundDetailsProvided
      ) {
        const paymentCheck = await checkPaymentStatus(orderId);

        if (
          paymentCheck.data?.warnings &&
          paymentCheck.data.warnings.length > 0
        ) {
          const warning = paymentCheck.data.warnings[0];

          if (warning.canBypass) {
            const proceed = window.confirm(
              `⚠️ ${warning.message}\n\n${warning.action}\n\nDo you want to proceed anyway?`
            );

            if (!proceed) return;
            options.forceConfirm = true;
          }
        }
      }

      // Proceed with update
      const updatePayload = {
        orderId,
        status: newStatus,
        comment: options.comment || `Status updated to ${newStatus}`,
        forceConfirm: options.forceConfirm,
        skipPaymentCheck: options.skipPaymentCheck,
      };

      // ✅ Add refund details if provided
      if (options.refundDetails) {
        updatePayload.refundDetails = options.refundDetails;
      }

      const result = await dispatch(
        updateOrderStatusThunk(updatePayload)
      ).unwrap();

      notifySuccess(`Order status updated to ${newStatus}`);

      // ✅ Show payment info if relevant
      if (result.data?.paymentInfo && !result.data.paymentInfo.fullyPaid) {
        notifyInfo(
          `Payment: ${
            result.data.paymentInfo.status
          } (₹${result.data.paymentInfo.amountDue.toLocaleString()} due)`,
          { duration: 5000 }
        );
      }

      // ✅ Show next steps
      if (result.data?.nextSteps) {
        notifyInfo(
          result.data.nextSteps.payment || result.data.nextSteps.message,
          { duration: 5000 }
        );
      }

      loadData();
    } catch (error) {
      // ✅ Enhanced error handling
      if (error.response?.data) {
        const err = error.response.data;

        if (err.critical || err.details?.paymentStatus) {
          // Payment validation error - show detailed message
          const message = (
            <div className="space-y-2">
              <p className="font-semibold text-red-700">{err.error}</p>
              {err.details?.message && (
                <p className="text-sm">{err.details.message}</p>
              )}
              {err.details && (
                <div className="bg-red-50 rounded p-2 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span>Payment Status:</span>
                    <span className="font-semibold">
                      {err.details.paymentStatus}
                    </span>
                  </div>
                  {err.details.amountDue > 0 && (
                    <div className="flex justify-between">
                      <span>Amount Due:</span>
                      <span className="font-semibold text-red-600">
                        ₹{err.details.amountDue.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {err.refundDue && (
                <p className="text-sm text-orange-600">
                  Refund Due: ₹{err.refundDue.toLocaleString()}
                </p>
              )}
              {err.action?.required && (
                <p className="text-xs text-blue-600 mt-2">
                  <strong>Action:</strong> {err.action.required}
                </p>
              )}
              <button
                onClick={() => navigate(`/orders/${orderId}`)}
                className="text-xs text-blue-600 underline hover:text-blue-800"
              >
                Go to order details →
              </button>
            </div>
          );
          notifyError(message, { duration: 10000 });
        } else {
          // Other errors
          notifyError(
            <div>
              <p className="font-semibold">{err.error}</p>
              {err.action?.hint && (
                <p className="text-xs text-gray-600 mt-1">{err.action.hint}</p>
              )}
            </div>,
            { duration: 6000 }
          );
        }
      } else {
        notifyError(error.message || "Failed to update status");
      }
    }
  };

  // ✅ NEW: Separate function for updating with refund details
  const handleUpdateStatusWithRefund = async (
    orderId,
    newStatus,
    refundDetails
  ) => {
    await handleUpdateStatus(orderId, newStatus, {
      refundDetails,
      refundDetailsProvided: true,
    });
  };

  // ============================================================================
  // 🔥 ENHANCED: Payment Update Handler with Auto-Progression Notification
  // ============================================================================
  const handleUpdatePayment = async (orderId, paymentData) => {
    try {
      const result = await dispatch(
        updatePaymentStatusThunk({
          orderId,
          paymentData,
        })
      ).unwrap();

      // ✅ Check if order status changed automatically
      if (result.data?.orderStatusChanged) {
        const { from, to, reason } = result.data.orderStatusChanged;

        notifySuccess(
          <div>
            <p className="font-semibold">Payment Updated Successfully!</p>
            <p className="text-sm mt-1">
              Order automatically progressed from <strong>{from}</strong> to{" "}
              <strong>{to}</strong>
            </p>
            {reason && <p className="text-xs text-gray-600 mt-1">{reason}</p>}
          </div>,
          { duration: 6000 }
        );
      } else {
        notifySuccess("Payment status updated");
      }

      // ✅ Show next steps if provided
      if (result.data?.nextSteps) {
        notifyInfo(
          result.data.nextSteps.message || result.data.nextSteps.nextAction,
          { duration: 5000 }
        );
      }

      loadData();
    } catch (error) {
      // ✅ Enhanced error handling
      if (error.response?.data) {
        const err = error.response.data;

        notifyError(
          <div className="space-y-2">
            <p className="font-semibold">{err.error}</p>
            {err.details?.message && (
              <p className="text-sm">{err.details.message}</p>
            )}
            {err.action?.required && (
              <p className="text-xs text-blue-600 mt-2">
                ✓ {err.action.required}
              </p>
            )}
            {err.action?.hint && (
              <button
                onClick={() => navigate(`/orders/${orderId}`)}
                className="text-xs text-blue-600 underline hover:text-blue-800 mt-1"
              >
                View order details →
              </button>
            )}
          </div>,
          { duration: 8000 }
        );
      } else {
        notifyError(error.message || "Failed to update payment");
      }
    }
  };

  // Handlers
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      status: "",
      paymentStatus: "",
      approvalStatus: "",
      refundStatus: "",
      branchCode: selectedBranch || "",
      fromDate: "",
      toDate: "",
      page: 1,
      limit: 20,
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleViewOrder = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const handleEditOrder = (orderId) => {
    navigate(`/orders/edit/${orderId}`);
  };

  const handleCreateOrder = () => {
    navigate("/consultation/start-session");
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ✅ UPDATED: Format refund status for display (remove REFUND_ prefix)
  const formatRefundStatus = (status) => {
    if (!status) return "";
    return status.replace("REFUND_", "");
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage customer orders and track fulfillment
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStatsModal(true)}
            className="flex items-center gap-2"
          >
            <DocumentTextIcon className="w-4 h-4" />
            Statistics
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateOrder}
            className="flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            New Order
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {orderStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            label="Total Orders"
            value={orderStats.summary?.totalOrders || 0}
            icon={ShoppingBagIcon}
            color="blue"
          />
          <StatCard
            label="Total Revenue"
            value={formatCurrency(orderStats.summary?.totalRevenue || 0)}
            icon={CurrencyRupeeIcon}
            color="green"
          />
          <StatCard
            label="Avg Order Value"
            value={formatCurrency(orderStats.summary?.avgOrderValue || 0)}
            icon={CurrencyRupeeIcon}
            color="purple"
          />
          <StatCard
            label="Pending Orders"
            value={orderStats.statusBreakdown?.PENDING || 0}
            icon={ClockIcon}
            color="yellow"
          />
          <StatCard
            label="Pending Refunds"
            value={
              (orderStats.refundBreakdown?.REFUND_PENDING || 0) +
              (orderStats.refundBreakdown?.REFUND_PENDING_APPROVAL || 0)
            }
            icon={RefreshIcon}
            color="orange"
          />
        </div>
      )}

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="space-y-4">
          {/* Search and Actions Row */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            {/* Search */}
            <div className="flex-1 relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order ID, customer name, phone..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={loadingList}
              >
                <RefreshIcon
                  className={`w-4 h-4 ${loadingList ? "animate-spin" : ""}`}
                />
              </Button>

              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "grid" ? "bg-white shadow" : ""
                  }`}
                  onClick={() => setViewMode("grid")}
                >
                  <ViewGridIcon className="w-4 h-4" />
                </button>
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "list" ? "bg-white shadow" : ""
                  }`}
                  onClick={() => setViewMode("list")}
                >
                  <ViewListIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="PROCESSING">Processing</option>
              <option value="READY">Ready</option>
              <option value="DISPATCHED">Dispatched</option>
              <option value="DELIVERED">Delivered</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            {/* Payment Status Filter */}
            <select
              value={filters.paymentStatus}
              onChange={(e) =>
                handleFilterChange("paymentStatus", e.target.value)
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Payment Status</option>
              <option value="PENDING">Pending</option>
              <option value="PARTIAL">Partial</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>

            {/* Approval Status Filter */}
            <select
              value={filters.approvalStatus}
              onChange={(e) =>
                handleFilterChange("approvalStatus", e.target.value)
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Approval Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>

            {/* ✅ UPDATED: Refund Status Filter with REFUND_ prefix */}
            <select
              value={filters.refundStatus}
              onChange={(e) =>
                handleFilterChange("refundStatus", e.target.value)
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Refund Status</option>
              <option value="REFUND_PENDING">Pending Refund</option>
              <option value="REFUND_PENDING_APPROVAL">Needs Approval</option>
              <option value="REFUND_APPROVED">Approved</option>
              <option value="REFUND_PROCESSING">Processing</option>
              <option value="REFUND_COMPLETED">Completed</option>
              <option value="REFUND_FAILED">Failed</option>
              <option value="REFUND_REJECTED">Rejected</option>
              <option value="REFUND_CANCELLED">Cancelled</option>
            </select>

            {/* Date From */}
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="From Date"
            />

            {/* Date To */}
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="To Date"
            />
          </div>

          {/* Clear Filters */}
          {(filters.search ||
            filters.status ||
            filters.paymentStatus ||
            filters.approvalStatus ||
            filters.refundStatus ||
            filters.fromDate ||
            filters.toDate) && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Orders Display */}
      {loadingList ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : !orders || orders.length === 0 ? (
        <EmptyState onAdd={handleCreateOrder} />
      ) : viewMode === "grid" ? (
        <GridView
          orders={orders}
          onView={handleViewOrder}
          onEdit={handleEditOrder}
          onUpdateStatus={handleUpdateStatus}
          onUpdatePayment={handleUpdatePayment}
          checkPaymentStatus={checkPaymentStatus}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          formatRefundStatus={formatRefundStatus}
        />
      ) : (
        <ListView
          orders={orders}
          onView={handleViewOrder}
          onEdit={handleEditOrder}
          onUpdateStatus={handleUpdateStatus}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          formatRefundStatus={formatRefundStatus}
        />
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="mt-6 flex justify-center">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {filters.page} of {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page === pagination.pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      <Modal
        isOpen={showStatsModal}
        closeModal={() => setShowStatsModal(false)}
        title="Order Statistics"
        maxWidth="2xl"
      >
        {orderStats && (
          <div className="space-y-6">
            {/* Summary */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Summary</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {orderStats.summary?.totalOrders || 0}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(orderStats.summary?.totalRevenue || 0)}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Avg Order Value</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(orderStats.summary?.avgOrderValue || 0)}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Items</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {orderStats.summary?.totalItems || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Status Breakdown */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(orderStats.statusBreakdown || {}).map(
                  ([status, count]) => (
                    <div
                      key={status}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant={getStatusColor(status)} size="sm">
                          {status}
                        </Badge>
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* ✅ UPDATED: Refund Breakdown with formatted status names */}
            {orderStats.refundBreakdown &&
              Object.keys(orderStats.refundBreakdown).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Refund Breakdown
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(orderStats.refundBreakdown || {}).map(
                      ([status, count]) => (
                        <div
                          key={status}
                          className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Badge variant={getRefundColor(status)} size="sm">
                              {formatRefundStatus(status)}
                            </Badge>
                          </div>
                          <span className="font-semibold">{count}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
      </Modal>

      {/* ✅ NEW: Refund Method Selection Modal */}
      <Modal
        isOpen={showRefundMethodModal}
        closeModal={() => {
          setShowRefundMethodModal(false);
          setPendingStatusChange(null);
          setPendingOrderId(null);
        }}
        title="Specify Refund Method"
        maxWidth="md"
      >
        <RefundMethodModal
          order={orders.find((o) => o.orderId === pendingOrderId)}
          refundMethod={refundMethod}
          setRefundMethod={setRefundMethod}
          refundBankDetails={refundBankDetails}
          setRefundBankDetails={setRefundBankDetails}
          refundUpiDetails={refundUpiDetails}
          setRefundUpiDetails={setRefundUpiDetails}
          onConfirm={handleConfirmWithRefundMethod}
          onCancel={() => {
            setShowRefundMethodModal(false);
            setPendingStatusChange(null);
            setPendingOrderId(null);
          }}
          formatCurrency={formatCurrency}
        />
      </Modal>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-8 h-8 text-${color}-600`} />
    </div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

// Empty State Component
const EmptyState = ({ onAdd }) => (
  <div className="text-center py-12 bg-white rounded-lg shadow">
    <ShoppingBagIcon className="mx-auto text-5xl text-gray-400 mb-4 w-16 h-16" />
    <p className="text-gray-500 mb-4">No orders found</p>
    <Button variant="primary" onClick={onAdd}>
      <PlusIcon className="w-4 h-4 mr-2" />
      Create First Order
    </Button>
  </div>
);

// Grid View Component
const GridView = ({
  orders,
  onView,
  onEdit,
  onUpdateStatus,
  onUpdatePayment,
  checkPaymentStatus,
  formatCurrency,
  formatDate,
  formatRefundStatus,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {orders.map((order) => (
      <div
        key={order._id}
        className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-4"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900">{order.orderId}</h3>
            <p className="text-sm text-gray-600">
              {order.userId?.name || order.guestCustomer?.name || "Guest"}
            </p>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <Badge variant={getStatusColor(order.status)} size="sm">
              {order.status}
            </Badge>
            {order.approval?.required && (
              <Badge variant="yellow" size="sm">
                Needs Approval
              </Badge>
            )}
            {/* ✅ UPDATED: Display formatted refund status */}
            {order.refundDetails?.required && (
              <Badge
                variant={getRefundColor(order.refundDetails.refundStatus)}
                size="sm"
              >
                {order.refundDetails.refundStatus === "REFUND_PENDING"
                  ? "⚠️ Refund Due"
                  : `Refund ${formatRefundStatus(
                      order.refundDetails.refundStatus
                    )}`}
              </Badge>
            )}
          </div>
        </div>

        {order.fulfillment?.type && (
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Fulfillment:</span>
            <div className="flex items-center gap-1">
              {order.fulfillment.type === "PICKUP" ? (
                <LocationMarkerIcon className="w-4 h-4 text-blue-600" />
              ) : (
                <TruckIcon className="w-4 h-4 text-green-600" />
              )}
              <span className="font-medium text-gray-900">
                {order.fulfillment.type}
              </span>
            </div>
          </div>
        )}

        {(order.fulfillment?.pickup?.scheduledDate ||
          order.fulfillment?.delivery?.scheduledDate) && (
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Scheduled:</span>
            <span className="text-gray-900">
              {formatDate(
                order.fulfillment.pickup?.scheduledDate ||
                  order.fulfillment.delivery?.scheduledDate
              )}
            </span>
          </div>
        )}

        {/* Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Order Value:</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(order.orderSummary?.finalTotals?.grandTotal)}
            </span>
          </div>

          {order.refundDetails?.required && (
            <div className="flex items-center justify-between text-sm bg-orange-50 -mx-2 px-2 py-1.5 rounded border border-orange-200">
              <span className="text-orange-700 font-medium flex items-center gap-1">
                <RefreshIcon className="w-3 h-3" />
                Refund Due:
              </span>
              <span className="font-bold text-orange-600">
                {formatCurrency(order.refundDetails.refundAmount)}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Items:</span>
            <span className="font-semibold text-gray-900">
              {order.orderSummary?.totalItems || 0}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Payment:</span>
            <Badge variant={getPaymentColor(order.payment?.status)} size="sm">
              {order.payment?.status}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Date:</span>
            <span className="text-gray-900">{formatDate(order.createdAt)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(order.orderId)}
            className="flex-1"
          >
            <EyeIcon className="w-4 h-4 mr-1" />
            View
          </Button>
          <QuickActionsMenu
            order={order}
            onView={onView}
            onEdit={onEdit}
            onUpdateStatus={onUpdateStatus}
            onUpdatePayment={onUpdatePayment}
            checkPaymentStatus={checkPaymentStatus}
            formatRefundStatus={formatRefundStatus}
          />
        </div>
      </div>
    ))}
  </div>
);

// List View Component
const ListView = ({
  orders,
  onView,
  onEdit,
  onUpdateStatus,
  formatCurrency,
  formatDate,
  formatRefundStatus,
}) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Order ID
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Payment
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Refund
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {orders.map((order) => (
            <tr key={order._id} className="hover:bg-gray-50">
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    {order.orderId}
                  </span>
                  {order.approval?.required && (
                    <ExclamationCircleIcon className="w-4 h-4 text-yellow-500" />
                  )}
                  {order.refundDetails?.required && (
                    <RefreshIcon className="w-4 h-4 text-orange-500" />
                  )}
                </div>
              </td>
              <td className="px-4 py-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {order.userId?.name || order.guestCustomer?.name || "Guest"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {order.userId?.phoneNumber ||
                      order.guestCustomer?.phoneNumber ||
                      "-"}
                  </p>
                </div>
              </td>
              <td className="px-4 py-4">
                <Badge variant={getStatusColor(order.status)} size="sm">
                  {order.status}
                </Badge>
              </td>
              <td className="px-4 py-4">
                <Badge
                  variant={getPaymentColor(order.payment?.status)}
                  size="sm"
                >
                  {order.payment?.status}
                </Badge>
              </td>
              <td className="px-4 py-4">
                {/* ✅ UPDATED: Display formatted refund status */}
                {order.refundDetails?.required ? (
                  <div className="flex flex-col gap-1">
                    <Badge
                      variant={getRefundColor(order.refundDetails.refundStatus)}
                      size="sm"
                    >
                      {formatRefundStatus(order.refundDetails.refundStatus)}
                    </Badge>
                    <span className="text-xs text-orange-600 font-medium">
                      ₹
                      {(order.refundDetails.refundAmount || 0).toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">-</span>
                )}
              </td>
              <td className="px-4 py-4">
                <span className="font-semibold text-gray-900">
                  {formatCurrency(order.orderSummary?.finalTotals?.grandTotal)}
                </span>
              </td>
              <td className="px-4 py-4">
                <span className="text-sm text-gray-600">
                  {formatDate(order.createdAt)}
                </span>
              </td>
              <td className="px-4 py-4">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onView(order.orderId)}
                  >
                    <EyeIcon className="w-4 h-4" />
                  </Button>
                  {order.status === "DRAFT" && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onEdit(order.orderId)}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ============================================================================
// 🔥 ENHANCED: Quick Actions Menu Component
// ============================================================================
const QuickActionsMenu = ({
  order,
  onUpdateStatus,
  onUpdatePayment,
  onEdit,
  onView,
  checkPaymentStatus,
  formatRefundStatus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Fetch payment status when menu opens
  useEffect(() => {
    if (isOpen && !paymentStatus) {
      setLoadingStatus(true);
      checkPaymentStatus(order.orderId)
        .then((status) => setPaymentStatus(status.data))
        .catch((err) => console.error("Failed to fetch payment status:", err))
        .finally(() => setLoadingStatus(false));
    }
  }, [isOpen, order.orderId, checkPaymentStatus]);

  const canEdit = order.status === "DRAFT";
  const canUpdateStatus = [
    "PENDING",
    "CONFIRMED",
    "PROCESSING",
    "READY",
  ].includes(order.status);
  const canUpdatePayment = !["COMPLETED", "REFUNDED"].includes(
    order.payment?.status
  );

  // ✅ Check if actions are blocked by payment
  const isDeliveryBlocked =
    paymentStatus && !paymentStatus.allowedActions?.canDeliver;
  const isProcessingBlocked =
    paymentStatus && !paymentStatus.allowedActions?.canProcess;

  const handleCompletePickup = async (orderId) => {
    try {
      await dispatch(
        completePickupThunk({
          orderId,
          pickedUpBy: {
            name: "Customer",
            phone: "N/A",
            relation: "Self",
          },
        })
      ).unwrap();
      notifySuccess("Pickup completed successfully");
    } catch (error) {
      notifyError(error || "Failed to complete pickup");
    }
  };

  return (
    <>
      {/* Three Dots Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <DotsVerticalIcon className="w-5 h-5 text-gray-600" />
      </button>

      {/* Overlay Menu */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-sm max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary-dark px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <ShoppingBagIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Order Actions</h3>
                  <p className="text-white text-opacity-80 text-xs">
                    {order.orderId}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <XCircleIcon className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Order Summary Card */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-5 py-3 border-b border-gray-200">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-gray-600 font-medium">Items</p>
                  <p className="text-lg font-bold text-gray-900">
                    {order.orderSummary?.totalItems || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium">Total</p>
                  <p className="text-lg font-bold text-green-600">
                    ₹
                    {(
                      order.orderSummary?.finalTotals?.grandTotal ||
                      order.orderSummary?.grandTotal ||
                      0
                    ).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium">Status</p>
                  <Badge
                    variant={getStatusColor(order.status)}
                    size="sm"
                    className="mt-1"
                  >
                    {order.status}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Scrollable Actions */}
            <div className="flex-1 overflow-y-auto">
              {/* ✅ Payment Status Card */}
              {paymentStatus && (
                <div className="px-4 pt-4 pb-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-600 font-medium">
                        Payment Status
                      </span>
                      <Badge
                        variant={getPaymentColor(order.payment?.status)}
                        size="sm"
                      >
                        {order.payment?.status}
                      </Badge>
                    </div>

                    {/* ✅ Show blockers */}
                    {paymentStatus.blockers &&
                      paymentStatus.blockers.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {paymentStatus.blockers.map((blocker, idx) => (
                            <div
                              key={idx}
                              className="bg-red-50 border border-red-200 rounded p-2"
                            >
                              <p className="text-xs text-red-700 font-medium">
                                🚫 {blocker.message}
                              </p>
                              <p className="text-xs text-red-600 mt-1">
                                {blocker.action}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                    {/* ✅ Show warnings */}
                    {paymentStatus.warnings &&
                      paymentStatus.warnings.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {paymentStatus.warnings.map((warning, idx) => (
                            <div
                              key={idx}
                              className="bg-yellow-50 border border-yellow-200 rounded p-2"
                            >
                              <p className="text-xs text-yellow-700">
                                ⚠️ {warning.message}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                    {/* ✅ Loading indicator */}
                    {loadingStatus && (
                      <div className="mt-2 flex items-center gap-2">
                        <ClockIcon className="w-4 h-4 text-blue-600 animate-spin" />
                        <p className="text-xs text-blue-600">
                          Checking payment...
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ✅ UPDATED: Refund Alert with formatted status */}
              {order.refundDetails?.required && (
                <div className="px-4 pb-2">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshIcon className="w-4 h-4 text-orange-600" />
                      <span className="text-xs font-semibold text-orange-900">
                        Refund Required
                      </span>
                      <Badge
                        variant={getRefundColor(
                          order.refundDetails.refundStatus
                        )}
                        size="sm"
                      >
                        {formatRefundStatus(order.refundDetails.refundStatus)}
                      </Badge>
                    </div>
                    <p className="text-xs text-orange-700">
                      ₹
                      {order.refundDetails.refundAmount.toLocaleString("en-IN")}{" "}
                      due to customer
                    </p>
                  </div>
                </div>
              )}

              {/* View & Edit Section */}
              <div className="p-4 space-y-2">
                <ActionButton
                  icon={EyeIcon}
                  label="View Order Details"
                  description="See full order information"
                  color="blue"
                  onClick={() => {
                    onView(order.orderId);
                    setIsOpen(false);
                  }}
                />

                {canEdit && (
                  <ActionButton
                    icon={PencilIcon}
                    label="Edit Order"
                    description="Modify order items and details"
                    color="indigo"
                    onClick={() => {
                      onEdit(order.orderId);
                      setIsOpen(false);
                    }}
                  />
                )}
              </div>

              {/* ✅ UPDATED: Refund Section with status checks */}
              {order.refundDetails?.required && (
                <>
                  <div className="px-4 pb-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <RefreshIcon className="w-4 h-4" />
                      Refund Management
                    </div>
                  </div>

                  <div className="px-4 pb-4">
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600 font-medium">
                          Refund Status
                        </span>
                        <Badge
                          variant={getRefundColor(
                            order.refundDetails.refundStatus
                          )}
                          size="sm"
                        >
                          {formatRefundStatus(order.refundDetails.refundStatus)}
                        </Badge>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-bold text-orange-700">
                          ₹
                          {order.refundDetails.refundAmount.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-gray-600">Method:</span>
                        <span className="text-gray-700">
                          {order.refundDetails.refundMethod || "Not specified"}
                        </span>
                      </div>
                    </div>

                    {/* ✅ UPDATED: Action buttons based on new statuses */}
                    {order.refundDetails.refundStatus ===
                      "REFUND_PENDING_APPROVAL" && (
                      <ActionButton
                        icon={CheckCircleIcon}
                        label="Approve Refund"
                        description="Approve refund for processing"
                        color="blue"
                        onClick={() => {
                          navigate(`/orders/${order.orderId}/refund/approve`);
                          setIsOpen(false);
                        }}
                      />
                    )}

                    {(order.refundDetails.refundStatus === "REFUND_APPROVED" ||
                      order.refundDetails.refundStatus ===
                        "REFUND_PENDING") && (
                      <ActionButton
                        icon={CurrencyRupeeIcon}
                        label="Process Refund"
                        description="Initiate refund payment"
                        color="green"
                        onClick={() => {
                          navigate(`/orders/${order.orderId}/refund/process`);
                          setIsOpen(false);
                        }}
                      />
                    )}

                    {order.refundDetails.refundStatus ===
                      "REFUND_PROCESSING" && (
                      <ActionButton
                        icon={CheckCircleIcon}
                        label="Complete Refund"
                        description="Mark refund as completed"
                        color="green"
                        onClick={() => {
                          navigate(`/orders/${order.orderId}/refund/complete`);
                          setIsOpen(false);
                        }}
                      />
                    )}

                    {["REFUND_PENDING", "REFUND_PENDING_APPROVAL"].includes(
                      order.refundDetails.refundStatus
                    ) && (
                      <ActionButton
                        icon={XCircleIcon}
                        label="Cancel Refund"
                        description="Cancel refund request"
                        color="red"
                        onClick={() => {
                          if (window.confirm("Cancel this refund request?")) {
                            dispatch(
                              cancelRefundThunk({
                                orderId: order.orderId,
                                reason: "Cancelled by admin",
                              })
                            );
                            setIsOpen(false);
                          }
                        }}
                      />
                    )}
                  </div>
                </>
              )}

              {/* Status Updates Section */}
              {canUpdateStatus && (
                <>
                  <div className="px-4 pb-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <ClockIcon className="w-4 h-4" />
                      Update Order Status
                    </div>
                  </div>
                  <div className="px-4 pb-4 space-y-2">
                    {order.status === "PENDING" && (
                      <>
                        <ActionButton
                          icon={CheckCircleIcon}
                          label="Confirm Order"
                          description={
                            order.refundDetails?.required
                              ? "⚠️ Will require refund method"
                              : "Accept and confirm this order"
                          }
                          color="green"
                          onClick={() => {
                            onUpdateStatus(order.orderId, "CONFIRMED");
                            setIsOpen(false);
                          }}
                        />
                        <ActionButton
                          icon={XCircleIcon}
                          label="Cancel Order"
                          description="Cancel and reject this order"
                          color="red"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to cancel this order?"
                              )
                            ) {
                              onUpdateStatus(order.orderId, "CANCELLED");
                              setIsOpen(false);
                            }
                          }}
                        />
                      </>
                    )}

                    {order.status === "CONFIRMED" && (
                      <ActionButton
                        icon={ClockIcon}
                        label="Start Processing"
                        description="Begin order processing"
                        color="blue"
                        onClick={() => {
                          onUpdateStatus(order.orderId, "PROCESSING");
                          setIsOpen(false);
                        }}
                      />
                    )}

                    {order.status === "PROCESSING" && (
                      <ActionButton
                        icon={CheckCircleIcon}
                        label="Mark as Ready"
                        description="Order is ready for pickup/delivery"
                        color="purple"
                        onClick={() => {
                          onUpdateStatus(order.orderId, "READY");
                          setIsOpen(false);
                        }}
                      />
                    )}

                    {order.status === "READY" &&
                      order.fulfillment?.type === "PICKUP" && (
                        <ActionButton
                          icon={ShoppingBagIcon}
                          label="Complete Pickup"
                          description="Customer has picked up the order"
                          color="green"
                          onClick={() => {
                            handleCompletePickup(order.orderId);
                            setIsOpen(false);
                          }}
                        />
                      )}

                    {/* ✅ Delivery action with payment block */}
                    {order.status === "READY" &&
                      order.fulfillment?.type === "DELIVERY" && (
                        <>
                          {isDeliveryBlocked ? (
                            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-semibold text-red-900 mb-1">
                                    Cannot Dispatch
                                  </p>
                                  <p className="text-xs text-red-700 mb-2">
                                    Payment must be completed before dispatch
                                  </p>
                                  <button
                                    onClick={() => {
                                      setShowPaymentModal(true);
                                      setIsOpen(false);
                                    }}
                                    className="text-xs text-blue-600 underline hover:text-blue-800"
                                  >
                                    Complete payment →
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <ActionButton
                              icon={TruckIcon}
                              label="Dispatch Order"
                              description="Send order for delivery"
                              color="indigo"
                              onClick={() => {
                                onUpdateStatus(order.orderId, "DISPATCHED");
                                setIsOpen(false);
                              }}
                            />
                          )}
                        </>
                      )}
                  </div>
                </>
              )}

              {/* Payment Section */}
              {canUpdatePayment && !order.refundDetails?.required && (
                <>
                  <div className="px-4 pb-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <CurrencyRupeeIcon className="w-4 h-4" />
                      Payment Status
                    </div>
                  </div>
                  <div className="px-4 pb-4">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600 font-medium">
                          Current Status
                        </span>
                        <Badge
                          variant={getPaymentColor(order.payment?.status)}
                          size="sm"
                        >
                          {order.payment?.status}
                        </Badge>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Amount Due:</span>
                        <span className="font-bold text-green-700">
                          ₹
                          {(order.orderSummary?.amountDue || 0).toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                    </div>
                    <ActionButton
                      icon={CurrencyRupeeIcon}
                      label="Update Payment"
                      description="Change payment status"
                      color="green"
                      onClick={() => {
                        setShowPaymentModal(true);
                        setIsOpen(false);
                      }}
                    />
                  </div>
                </>
              )}

              {/* Customer Info */}
              <div className="px-4 pb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <UserIcon className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-blue-800 font-medium">
                        Customer
                      </p>
                      <p className="text-sm text-blue-900 font-semibold truncate">
                        {order.userId?.name ||
                          order.guestCustomer?.name ||
                          "Guest"}
                      </p>
                      {order.fulfillment?.type && (
                        <p className="text-xs text-blue-700 mt-1 flex items-center gap-1">
                          {order.fulfillment.type === "PICKUP" ? (
                            <LocationMarkerIcon className="w-3 h-3" />
                          ) : (
                            <TruckIcon className="w-3 h-3" />
                          )}
                          {order.fulfillment.type}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-5 py-3 border-t border-gray-200">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status Modal */}
      {showPaymentModal && (
        <PaymentStatusModal
          order={order}
          onClose={() => setShowPaymentModal(false)}
          onUpdate={onUpdatePayment}
          checkPaymentStatus={checkPaymentStatus}
        />
      )}
    </>
  );
};

// Action Button Component
const ActionButton = ({ icon: Icon, label, description, color, onClick }) => {
  const colorClasses = {
    blue: "bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700",
    green: "bg-green-50 hover:bg-green-100 border-green-200 text-green-700",
    red: "bg-red-50 hover:bg-red-100 border-red-200 text-red-700",
    purple:
      "bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700",
    indigo:
      "bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-700",
    orange:
      "bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700",
  };

  const iconColorClasses = {
    blue: "text-blue-600",
    green: "text-green-600",
    red: "text-red-600",
    purple: "text-purple-600",
    indigo: "text-indigo-600",
    orange: "text-orange-600",
  };

  return (
    <button
      onClick={onClick}
      className={`w-full p-3 rounded-lg border transition-all flex items-start gap-3 text-left ${colorClasses[color]}`}
    >
      <div
        className={`p-2 rounded-lg bg-white shadow-sm ${iconColorClasses[color]}`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{label}</p>
        <p className="text-xs text-gray-600 mt-0.5">{description}</p>
      </div>
      <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
    </button>
  );
};

// ============================================================================
// 🔥 NEW: Refund Method Modal Component
// ============================================================================
const RefundMethodModal = ({
  order,
  refundMethod,
  setRefundMethod,
  refundBankDetails,
  setRefundBankDetails,
  refundUpiDetails,
  setRefundUpiDetails,
  onConfirm,
  onCancel,
  formatCurrency,
}) => {
  if (!order) return null;

  return (
    <div className="space-y-4">
      {/* Alert */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <RefreshIcon className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-orange-900 mb-1">
              Refund Required Before Confirmation
            </p>
            <p className="text-sm text-orange-800">
              This order has a refund due of{" "}
              <strong className="text-orange-900">
                {formatCurrency(order.refundDetails?.refundAmount || 0)}
              </strong>
              . Please specify how the refund will be processed.
            </p>
          </div>
        </div>
      </div>

      {/* Refund Method Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Refund Method *
        </label>
        <div className="space-y-2">
          {/* Cash */}
          <button
            onClick={() => setRefundMethod("CASH")}
            className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
              refundMethod === "CASH"
                ? "border-green-500 bg-green-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div
              className={`p-1.5 rounded-lg ${
                refundMethod === "CASH" ? "bg-green-100" : "bg-gray-100"
              }`}
            >
              <CurrencyRupeeIcon
                className={`w-4 h-4 ${
                  refundMethod === "CASH" ? "text-green-600" : "text-gray-400"
                }`}
              />
            </div>
            <div className="flex-1 text-left">
              <span
                className={`font-medium text-sm block ${
                  refundMethod === "CASH" ? "text-green-700" : "text-gray-700"
                }`}
              >
                Cash Refund
              </span>
              <p className="text-xs text-gray-600">Refund in cash at store</p>
            </div>
            {refundMethod === "CASH" && (
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            )}
          </button>

          {/* Bank Transfer */}
          <button
            onClick={() => setRefundMethod("BANK_TRANSFER")}
            className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
              refundMethod === "BANK_TRANSFER"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div
              className={`p-1.5 rounded-lg ${
                refundMethod === "BANK_TRANSFER" ? "bg-blue-100" : "bg-gray-100"
              }`}
            >
              <DocumentTextIcon
                className={`w-4 h-4 ${
                  refundMethod === "BANK_TRANSFER"
                    ? "text-blue-600"
                    : "text-gray-400"
                }`}
              />
            </div>
            <div className="flex-1 text-left">
              <span
                className={`font-medium text-sm block ${
                  refundMethod === "BANK_TRANSFER"
                    ? "text-blue-700"
                    : "text-gray-700"
                }`}
              >
                Bank Transfer
              </span>
              <p className="text-xs text-gray-600">
                Transfer to customer's account
              </p>
            </div>
            {refundMethod === "BANK_TRANSFER" && (
              <CheckCircleIcon className="w-5 h-5 text-blue-600" />
            )}
          </button>

          {/* UPI */}
          <button
            onClick={() => setRefundMethod("UPI")}
            className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
              refundMethod === "UPI"
                ? "border-purple-500 bg-purple-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div
              className={`p-1.5 rounded-lg ${
                refundMethod === "UPI" ? "bg-purple-100" : "bg-gray-100"
              }`}
            >
              <CurrencyRupeeIcon
                className={`w-4 h-4 ${
                  refundMethod === "UPI" ? "text-purple-600" : "text-gray-400"
                }`}
              />
            </div>
            <div className="flex-1 text-left">
              <span
                className={`font-medium text-sm block ${
                  refundMethod === "UPI" ? "text-purple-700" : "text-gray-700"
                }`}
              >
                UPI Transfer
              </span>
              <p className="text-xs text-gray-600">Send via UPI</p>
            </div>
            {refundMethod === "UPI" && (
              <CheckCircleIcon className="w-5 h-5 text-purple-600" />
            )}
          </button>
        </div>
      </div>

      {/* Bank Details Form */}
      {refundMethod === "BANK_TRANSFER" && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-3">
          <p className="text-sm font-medium text-blue-900 mb-2">
            Bank Account Details
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Account Holder Name *
            </label>
            <input
              type="text"
              value={refundBankDetails.accountName}
              onChange={(e) =>
                setRefundBankDetails({
                  ...refundBankDetails,
                  accountName: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Enter account holder name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Account Number *
            </label>
            <input
              type="text"
              value={refundBankDetails.accountNumber}
              onChange={(e) =>
                setRefundBankDetails({
                  ...refundBankDetails,
                  accountNumber: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Enter account number"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                IFSC Code *
              </label>
              <input
                type="text"
                value={refundBankDetails.ifscCode}
                onChange={(e) =>
                  setRefundBankDetails({
                    ...refundBankDetails,
                    ifscCode: e.target.value.toUpperCase(),
                  })
                }
                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm uppercase"
                placeholder="IFSC"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Bank Name *
              </label>
              <input
                type="text"
                value={refundBankDetails.bankName}
                onChange={(e) =>
                  setRefundBankDetails({
                    ...refundBankDetails,
                    bankName: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Bank name"
              />
            </div>
          </div>
        </div>
      )}

      {/* UPI Details Form */}
      {refundMethod === "UPI" && (
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 space-y-3">
          <p className="text-sm font-medium text-purple-900 mb-2">
            UPI Details
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              UPI ID *
            </label>
            <input
              type="text"
              value={refundUpiDetails.vpa}
              onChange={(e) =>
                setRefundUpiDetails({
                  ...refundUpiDetails,
                  vpa: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              placeholder="example@upi"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Account Holder Name (Optional)
            </label>
            <input
              type="text"
              value={refundUpiDetails.verifiedName}
              onChange={(e) =>
                setRefundUpiDetails({
                  ...refundUpiDetails,
                  verifiedName: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              placeholder="Enter name"
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm}>
          Confirm Order with Refund Method
        </Button>
      </div>
    </div>
  );
};

// Payment Status Modal Component (placeholder - use your existing implementation)
const PaymentStatusModal = ({
  order,
  onClose,
  onUpdate,
  checkPaymentStatus,
}) => {
  // Use your existing PaymentStatusModal implementation
  return null;
};

// ✅ UPDATED: Helper functions with new status names
const getStatusColor = (status) => {
  const colors = {
    DRAFT: "gray",
    PENDING_APPROVAL: "yellow",
    APPROVED: "green",
    REJECTED: "red",
    PENDING: "blue",
    CONFIRMED: "green",
    PROCESSING: "blue",
    READY: "purple",
    DISPATCHED: "indigo",
    DELIVERED: "green",
    CANCELLED: "red",
    REFUNDED: "orange",
  };
  return colors[status] || "gray";
};

const getPaymentColor = (status) => {
  const colors = {
    PENDING: "yellow",
    PARTIAL: "orange",
    COMPLETED: "green",
    FAILED: "red",
    REFUNDED: "purple",
  };
  return colors[status] || "gray";
};

// ✅ UPDATED: Refund color helper with REFUND_ prefixed statuses
const getRefundColor = (status) => {
  const colors = {
    REFUND_PENDING: "orange",
    REFUND_PENDING_APPROVAL: "yellow",
    REFUND_APPROVED: "blue",
    REFUND_INITIATED: "indigo", // Legacy status
    REFUND_PROCESSING: "indigo",
    REFUND_COMPLETED: "green",
    REFUND_FAILED: "red",
    REFUND_REJECTED: "red",
    REFUND_CANCELLED: "gray",
  };
  return colors[status] || "gray";
};

export default OrderList;
